import { getDbSupabase } from './supabase';
import { formatDeliveryDateDisplay, parseDeliveryDateForDb } from './demDeliveryDate';
import { formatSupabaseError } from './formatSupabaseError';
import { collectCustomerCodesFromRows, fetchCustomerNamesByCodes } from './demCustomerNames';
import {
  assertDemActorReady,
  fetchDemUserDisplayNameMap,
  resolveDemDisplayUser,
} from './demUserNames';
import { formatCheckedAtDisplay } from './demOrderChecked';
import type { DemNotifyOrder, DemPoOrder, DemPoOrderFilter, DemPoOrderItem } from '../types/dem';
import {
  sendPendingOrderNotifications,
  sendProcessedOrderNotifications,
  type PendingNotificationResult,
  type ProcessedNotificationResult,
} from './demOrderNotifications';
import { rowsToSavePayload } from './demPoMapping';
import type { DemPoExtractHeader, DemPoReviewRow } from '../types/dem';
import {
  formatOrderCreatorDisplay,
  formatOrderExporterDisplay,
  parseOrderBatches,
} from './demOrderBatches';
import { buildBatchAwareExportRows, finalizeOrderExport } from './demOrderExport';

const SGT = 'Asia/Singapore';
const ORDER_SELECT =
  'order_id, created_at, delivery_date, customer_code, ref_no, status, created_by, exported_by, checked_at, checked_by, items:order_items(item_code, description, qty, uom, batch_id), batches:order_batches(id, order_id, batch_no, reason, created_by, created_at, exported_by, exported_at)';

const INITIAL_PAGE = 50;
const MORE_PAGE = 100;

function parseCreatedAtDisplay(raw: string | null | undefined): string {
  if (!raw) return '—';
  try {
    const normalized = raw.includes('T') ? raw.replace('Z', '+00:00') : `${raw.replace(' ', 'T')}+00:00`;
    const date = new Date(normalized);
    if (Number.isNaN(date.getTime())) return raw;
    return new Intl.DateTimeFormat('en-GB', {
      timeZone: SGT,
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(date);
  } catch {
    return raw;
  }
}

function parseDeliveryDateDisplayFromDb(raw: string | null | undefined): string {
  return formatDeliveryDateDisplay(raw);
}

function parseItems(raw: unknown): DemPoOrderItem[] {
  if (!Array.isArray(raw)) return [];
  return raw.map((row) => {
    const r = row as Record<string, unknown>;
    return {
      item_code: String(r.item_code ?? '').trim(),
      description: String(r.description ?? '').trim(),
      qty: Number(r.qty ?? 0),
      uom: String(r.uom ?? '').trim(),
      batch_id: r.batch_id == null ? null : String(r.batch_id).trim() || null,
    };
  });
}

function mapOrderRow(
  row: Record<string, unknown>,
  customerNames: Map<string, string>,
  userNames: Map<string, string>,
): DemPoOrder {
  const customerCode = String(row.customer_code ?? '').trim();
  const customerName =
    (customerCode ? customerNames.get(customerCode) : null) ?? 'Unknown';
  const createdByFallback = String(row.created_by ?? '').trim();
  const batchesRaw = parseOrderBatches(row.batches);
  const batches = batchesRaw.map((batch) => ({
    ...batch,
    created_by: resolveDemDisplayUser(
      batch.created_by.trim() || (batch.batch_no === 0 ? createdByFallback : ''),
      userNames,
    ),
    exported_by: batch.exported_by
      ? resolveDemDisplayUser(batch.exported_by, userNames)
      : null,
  }));

  return {
    order_id: String(row.order_id ?? ''),
    customer_code: customerCode,
    customer_name: customerName,
    created_at: String(row.created_at ?? ''),
    created_at_display: parseCreatedAtDisplay(String(row.created_at ?? '')),
    delivery_date: String(row.delivery_date ?? ''),
    delivery_date_display: parseDeliveryDateDisplayFromDb(String(row.delivery_date ?? '')),
    ref_no: String(row.ref_no ?? '').trim() || '—',
    status: String(row.status ?? ''),
    created_by: formatOrderCreatorDisplay(batchesRaw, userNames, createdByFallback),
    exported_by: formatOrderExporterDisplay(batchesRaw, userNames, ''),
    checked_at: row.checked_at ? String(row.checked_at) : null,
    checked_at_display: formatCheckedAtDisplay(row.checked_at ? String(row.checked_at) : null),
    checked_by: resolveDemDisplayUser(String(row.checked_by ?? ''), userNames),
    items: parseItems(row.items),
    batches,
  };
}

async function mapOrderRows(rows: Record<string, unknown>[]): Promise<DemPoOrder[]> {
  const [customerNames, userNames] = await Promise.all([
    fetchCustomerNamesByCodes(collectCustomerCodesFromRows(rows)),
    fetchDemUserDisplayNameMap(),
  ]);
  return rows.map((row) => mapOrderRow(row, customerNames, userNames));
}

export function todayDisplaySgt(): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: SGT,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(new Date());
}

export async function fetchPoOrdersPage(
  offset: number,
  limit: number,
  searchKw?: string,
): Promise<DemPoOrder[]> {
  const db = getDbSupabase('dem');

  let query = db.from('orders').select(ORDER_SELECT).eq('source', 'Desktop');

  const kw = searchKw?.trim();
  if (kw) {
    query = query.or(`order_id.ilike.%${kw}%,ref_no.ilike.%${kw}%,customer_code.ilike.%${kw}%`);
  } else {
    query = query.neq('status', 'Archived');
  }

  const { data, error } = await query
    .order('created_at', { ascending: false })
    .range(offset, offset + limit - 1);

  if (error) throw new Error(formatSupabaseError(error));

  return mapOrderRows((data ?? []) as Record<string, unknown>[]);
}

export function mergePoOrders(existing: DemPoOrder[], incoming: DemPoOrder[]): DemPoOrder[] {
  const map = new Map(existing.map((order) => [order.order_id, order]));
  for (const order of incoming) {
    map.set(order.order_id, order);
  }
  return [...map.values()].sort((a, b) => b.order_id.localeCompare(a.order_id));
}

export function filterPoOrdersClient(
  orders: DemPoOrder[],
  search: string,
  statusFilter: DemPoOrderFilter,
): DemPoOrder[] {
  const keywords = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const today = todayDisplaySgt();

  return orders.filter((order) => {
    if (statusFilter === 'Pending' && order.status !== 'Pending') return false;
    if (
      statusFilter === 'TodayProcessed' &&
      (order.status !== 'Processed' || !order.created_at_display.includes(today))
    ) {
      return false;
    }
    if (statusFilter === 'NeedsCheck' && (order.status !== 'Processed' || order.checked_at)) {
      return false;
    }

    if (keywords.length === 0) return true;

    const itemsStr = order.items
      .map((item) => `${item.item_code} ${item.description}`)
      .join(' ');
    const pool = [
      order.order_id,
      order.ref_no,
      order.customer_code,
      order.customer_name,
      order.status,
      order.created_by,
      order.exported_by,
      order.checked_by,
      itemsStr,
    ]
      .join(' ')
      .toLowerCase();

    return keywords.every((kw) => pool.includes(kw));
  });
}

export function poOrderToNotifyOrder(order: DemPoOrder): DemNotifyOrder {
  return {
    order_id: order.order_id,
    customer_code: order.customer_code,
    customer_name: order.customer_name,
    ref_no: order.ref_no,
    delivery_date_display: order.delivery_date_display,
    email: '',
    items: order.items,
  };
}

async function fetchPendingPoOrders(createdByIds?: string[]): Promise<DemPoOrder[]> {
  const db = getDbSupabase('dem');

  let query = db
    .from('orders')
    .select(ORDER_SELECT)
    .eq('status', 'Pending')
    .eq('source', 'Desktop');

  const owners = (createdByIds ?? []).map((id) => id.trim()).filter(Boolean);
  // Empty owners must not mean “everyone’s pending”.
  if (owners.length === 0) return [];
  if (owners.length === 1) {
    query = query.eq('created_by', owners[0]!);
  } else {
    query = query.in('created_by', owners);
  }

  const { data, error } = await query;

  if (error) throw new Error(formatSupabaseError(error));

  return mapOrderRows((data ?? []) as Record<string, unknown>[]);
}

export type CloudExportResult = {
  exported: number;
  notifications: ProcessedNotificationResult;
};

export async function exportPendingPoOrdersToCloudQueue(
  exportedBy: string,
  createdByIds?: string[],
): Promise<CloudExportResult> {
  assertDemActorReady(exportedBy);
  const orders = await fetchPendingPoOrders(createdByIds ?? [exportedBy]);
  if (orders.length === 0) {
    return { exported: 0, notifications: { sent: 0, skippedNoEmail: 0, failed: [] } };
  }

  const { hdrRows, detRows, orderIds, batchIds } = buildBatchAwareExportRows(orders);
  if (orderIds.length === 0 || (hdrRows.length === 0 && detRows.length === 0)) {
    return { exported: 0, notifications: { sent: 0, skippedNoEmail: 0, failed: [] } };
  }

  const batchId = `PO_${new Date().toISOString().replace(/[-:TZ.]/g, '').slice(0, 14)}_${Math.random().toString(36).slice(2, 6).toUpperCase()}`;
  const db = getDbSupabase('dem');

  const { error: queueError } = await db.from('export_queue').insert({
    batch_id: batchId,
    target_folder: 'sales_order_B2B',
    order_ids: orderIds,
    exported_by: exportedBy,
    hdr_rows: hdrRows,
    det_rows: detRows,
    status: 'pending',
  });
  if (queueError) throw new Error(formatSupabaseError(queueError));

  await finalizeOrderExport({ orderIds, batchIds, exportedBy });

  const notifications = await sendProcessedOrderNotifications(
    orders.filter((o) => orderIds.includes(o.order_id)).map(poOrderToNotifyOrder),
  );

  return { exported: orderIds.length, notifications };
}

export async function voidPoOrder(orderId: string, voidedBy: string): Promise<void> {
  const db = getDbSupabase('dem');
  const { error } = await db
    .from('orders')
    .update({
      status: 'Cancelled',
      exported_by: `${voidedBy} (Void)`,
    })
    .eq('order_id', orderId);

  if (error) throw new Error(formatSupabaseError(error));
}

export {
  markOrderChecked as markPoOrderChecked,
  unmarkOrderChecked as unmarkPoOrderChecked,
} from './demOrderChecked';

export function poOrderStatusTone(status: string): 'pending' | 'uploading' | 'processed' | 'cancelled' | 'default' {
  const s = status.toLowerCase();
  if (s === 'pending') return 'pending';
  if (s === 'uploading') return 'uploading';
  if (s === 'processed') return 'processed';
  if (s === 'cancelled') return 'cancelled';
  return 'default';
}

export function poOrderStatusIcon(status: string): string {
  const s = status.toLowerCase();
  if (s === 'processed') return '✓';
  if (s === 'uploading') return '⏳';
  if (s === 'cancelled') return '✕';
  if (s === 'archived') return '🗄';
  return '📋';
}

export const PO_ORDERS_INITIAL_PAGE = INITIAL_PAGE;
export const PO_ORDERS_MORE_PAGE = MORE_PAGE;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

async function getNextPoNumber(): Promise<string> {
  const db = getDbSupabase('dem');
  const maxRetries = 3;

  for (let attempt = 0; attempt < maxRetries; attempt += 1) {
    const { data, error } = await db.rpc('generate_po_number');
    if (error) throw new Error(formatSupabaseError(error));
    const po = String(data ?? '').trim();
    if (po && po.toLowerCase() !== 'none') return po;
    if (attempt === maxRetries - 1) {
      throw new Error('Failed to generate PO number from server.');
    }
  }

  throw new Error('Failed to generate PO number from server.');
}

export async function checkPoDuplicate(poNumber: string): Promise<{ duplicate: boolean; message: string }> {
  const target = poNumber.trim();
  if (!target || target.length < 3) {
    return { duplicate: false, message: '' };
  }

  const db = getDbSupabase('dem');
  const { data, error } = await db
    .from('orders')
    .select('created_at, status')
    .ilike('ref_no', target)
    .limit(1);

  if (error) throw new Error(formatSupabaseError(error));
  if (!data || data.length === 0) {
    return { duplicate: false, message: '' };
  }

  const row = data[0] as { created_at?: string; status?: string };
  const dateStr = String(row.created_at ?? '').slice(0, 10) || 'unknown date';
  const status = String(row.status ?? 'Unknown');
  return {
    duplicate: true,
    message: `Found in cloud database (Status: ${status}, Date: ${dateStr})`,
  };
}

export type SavePoOrderResult = {
  order_id: string;
  notifications: PendingNotificationResult;
};

export async function savePoOrderToPending(
  header: DemPoExtractHeader,
  rows: DemPoReviewRow[],
  createdBy: string,
  processingSeconds?: number | null,
): Promise<SavePoOrderResult> {
  assertDemActorReady(createdBy);
  const items = rowsToSavePayload(rows);
  if (items.length === 0) {
    throw new Error('No valid line items to save. Map items or mark rows as IGNORE.');
  }

  const customerCode = header.sys_customer_code?.trim() ?? '';
  if (!customerCode) {
    throw new Error('System customer code is required.');
  }

  const { assertOrderWithinBlanketOutstanding } = await import('./demBlanketOrders');
  await assertOrderWithinBlanketOutstanding({
    poNumber: header.po_number?.trim() ?? '',
    lines: items,
  });

  const db = getDbSupabase('dem');
  let orderId = await getNextPoNumber();
  const dbDate = parseDeliveryDateForDb(header.delivery_date);

  const orderPayload: Record<string, unknown> = {
    order_id: orderId,
    ref_no: header.po_number?.trim() ?? '',
    customer_code: customerCode,
    status: 'Pending',
    source: 'Desktop',
    created_by: createdBy,
  };
  if (dbDate) orderPayload.delivery_date = dbDate;
  if (processingSeconds != null && processingSeconds > 0) {
    orderPayload.processing_seconds = Math.floor(processingSeconds);
  }

  const maxRetries = 3;
  let inserted = false;

  for (let attempt = 0; attempt < maxRetries; attempt += 1) {
    const { error } = await db.from('orders').insert(orderPayload);
    if (!error) {
      inserted = true;
      break;
    }

    const errStr = formatSupabaseError(error).toLowerCase();
    if (errStr.includes('23505') || errStr.includes('duplicate')) {
      const { data: existing } = await db
        .from('orders')
        .select('ref_no, customer_code')
        .eq('order_id', orderId)
        .maybeSingle();

      const row = existing as { ref_no?: string; customer_code?: string } | null;
      if (
        row &&
        String(row.ref_no ?? '') === String(orderPayload.ref_no ?? '') &&
        String(row.customer_code ?? '') === String(orderPayload.customer_code ?? '')
      ) {
        inserted = true;
        break;
      }

      if (attempt === maxRetries - 1) {
        throw new Error(`Duplicate PO clash for ${orderId}. Please try again.`);
      }

      orderId = await getNextPoNumber();
      orderPayload.order_id = orderId;
      continue;
    }

    if (attempt === maxRetries - 1) throw new Error(formatSupabaseError(error));
    await sleep(1500);
  }

  if (!inserted) {
    throw new Error('Failed to insert order header into database.');
  }

  const itemsPayload = items.map((item) => ({
    order_id: orderId,
    item_code: item.item_code,
    description: item.description,
    qty: item.qty,
    uom: item.uom,
  }));

  for (let attempt = 0; attempt < maxRetries; attempt += 1) {
    if (attempt > 0) {
      await db.from('order_items').delete().eq('order_id', orderId);
    }

    const { error } = await db.from('order_items').insert(itemsPayload);
    if (!error) break;
    if (attempt === maxRetries - 1) throw new Error(formatSupabaseError(error));
    await sleep(1500);
  }

  const deliveryDisplay = dbDate
    ? formatDeliveryDateDisplay(dbDate)
    : header.delivery_date?.trim() || '—';

  const notifications = await sendPendingOrderNotifications([
    {
      order_id: orderId,
      customer_code: customerCode,
      customer_name: header.sys_customer_name?.trim() || customerCode,
      ref_no: header.po_number?.trim() || '—',
      delivery_date_display: deliveryDisplay,
      email: '',
      items,
    },
  ]);

  return { order_id: orderId, notifications };
}
